import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Copy, ScanFace } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import AccountFaceBiometrics from "components/AccountFaceBiometrics";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Switch } from "components/ui/switch";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "pt", label: "Português" },
  { value: "es", label: "Español" },
  { value: "it", label: "Italiano" },
];

function CredentialsModal({ email, password, mfaSecret, mfaQr, title, onClose }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const text = password ? `${email} / ${password}` : (mfaSecret || email);
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-md mx-4 animate-fade-in border border-border max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
            <Check className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="text-lg font-semibold text-center mb-1">{title || t("account.registered")}</h2>
          <p className="text-sm text-muted-foreground text-center mb-4">{t("contestant.saveNote")}</p>
          <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">{t("login.email")}</span>
              <span className="font-medium break-all">{email}</span>
            </div>
            {password && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">{t("contestant.initialPassword")}</span>
                <span className="font-mono font-medium break-all">{password}</span>
              </div>
            )}
          </div>

          {mfaQr && (
            <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4">
              <p className="text-sm font-medium mb-1">Autenticação em duas etapas (MFA)</p>
              <p className="text-xs text-muted-foreground mb-3">
                Escaneie o QR no app autenticador (Google Authenticator, Authy…) ou use o segredo manualmente.
              </p>
              <div className="flex flex-col items-center gap-3">
                <img src={mfaQr} alt="MFA QR" className="h-40 w-40 rounded bg-white p-2" />
                {mfaSecret && (
                  <code className="text-xs break-all text-center text-muted-foreground">{mfaSecret}</code>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-between gap-3 mt-6">
            <Button variant="outline" onClick={copy}>
              <Copy className="h-4 w-4 mr-2" />
              {copied ? t("contestant.copied") : t("contestant.copy")}
            </Button>
            <Button onClick={onClose}>{t("contestant.done")}</Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function AccountForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const isEditing = Boolean(id);
  const isSuperAdmin = !!user?.capabilities?.manage_system;

  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("liveness");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [language, setLanguage] = useState("pt");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [mustChange, setMustChange] = useState(false);
  const [cannotChange, setCannotChange] = useState(false);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [error, setError] = useState("");
  const [credentials, setCredentials] = useState(null);
  const [mfaModal, setMfaModal] = useState(null);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get("/api/companies/").then((res) => setCompanies(res.data)).catch(() => {});
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isEditing) return;
    api.get(`/api/accounts/${id}/`)
      .then((res) => {
        const a = res.data;
        setEmail(a.email || "");
        setRole(a.is_super ? "super_admin" : (a.role || "liveness"));
        setFirstName(a.first_name || "");
        setLastName(a.last_name || "");
        setMobilePhone(a.mobile_phone || "");
        setLanguage(a.language || "pt");
        setCountry(a.country || "");
        setState(a.state || "");
        setCity(a.city || "");
        setCompany(a.company || "");
        setMustChange(!!a.must_change_pass);
        setCannotChange(!!a.cannot_change_pass);
      })
      .catch(() => setError(t("account.loadError")))
      .finally(() => setFetching(false));
  }, [id, isEditing, t]);

  const toggleMust = (v) => { setMustChange(v); if (v) setCannotChange(false); };
  const toggleCannot = (v) => { setCannotChange(v); if (v) setMustChange(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const isSuperRole = role === "super_admin";
    const payload = {
      email,
      role: isSuperRole ? "institution_admin" : role,
      is_super: isSuperRole,
      first_name: firstName, last_name: lastName, mobile_phone: mobilePhone,
      language, country, state, city,
      must_change_pass: mustChange, cannot_change_pass: cannotChange,
      // A super-admin account is cross-tenant — never send a company for it.
      ...(isSuperAdmin && !isSuperRole ? { company } : {}),
    };
    try {
      if (isEditing) {
        await api.put(`/api/accounts/${id}/`, payload);
        navigate("/liveness/admin/accounts");
      } else {
        const res = await api.post("/api/accounts/", payload);
        setCredentials({
          email: res.data.email, password: res.data.initial_password,
          mfaSecret: res.data.mfa_secret, mfaQr: res.data.mfa_qr,
        });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("account.saveError"));
    } finally {
      setLoading(false);
    }
  };

  const resetMfa = async () => {
    setError("");
    try {
      const res = await api.post(`/api/accounts/${id}/reset-mfa/`);
      setMfaModal({ email, mfaSecret: res.data.mfa_secret, mfaQr: res.data.mfa_qr });
    } catch {
      setError(t("account.saveError"));
    }
  };

  return (
    <div className="animate-fade-in">
      <button onClick={() => navigate("/liveness/admin/accounts")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" />{t("common.back")}
      </button>

      <h1 className="text-2xl font-bold tracking-tight mb-1">
        {isEditing ? t("account.edit") : t("account.new")}
      </h1>
      <p className="text-sm text-muted-foreground mb-6">{t("account.newSubtitle")}</p>

      <Card className="w-full">
        <CardContent className="pt-6">
          {fetching ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* A Super Admin is cross-tenant — no company applies. */}
            {isSuperAdmin && role !== "super_admin" && (
              <div className="space-y-2">
                <Label htmlFor="acc-company">{t("common.company")}</Label>
                <select id="acc-company" value={company} onChange={(e) => setCompany(e.target.value)} required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  <option value="">{t("common.selectCompany")}</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="acc-email">{t("login.email")}</Label>
                <Input id="acc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
              </div>
              <div className="space-y-2">
                <Label htmlFor="acc-role">{t("account.role")}</Label>
                <select id="acc-role" value={role} onChange={(e) => setRole(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  <option value="liveness">{t("account.role.liveness")}</option>
                  <option value="institution_admin">{t("account.role.institution_admin")}</option>
                  {isSuperAdmin && <option value="super_admin">{t("account.role.super_admin", "Super Admin")}</option>}
                </select>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="acc-first">{t("common.firstName")}</Label>
                <Input id="acc-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="acc-last">{t("common.lastName")}</Label>
                <Input id="acc-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="acc-phone">{t("common.phone")}</Label>
                <Input id="acc-phone" value={mobilePhone} onChange={(e) => setMobilePhone(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="acc-language">{t("common.language")}</Label>
                <select id="acc-language" value={language} onChange={(e) => setLanguage(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="acc-country">{t("common.country")}</Label>
                <Input id="acc-country" value={country}
                  onChange={(e) => setCountry(e.target.value.replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase())}
                  placeholder={t("common.country.placeholder")} maxLength={2} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="acc-state">{t("common.state", "State")}</Label>
                <Input id="acc-state" value={state} onChange={(e) => setState(e.target.value.slice(0, 100))} maxLength={100} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="acc-city">{t("common.city", "City")}</Label>
                <Input id="acc-city" value={city} onChange={(e) => setCity(e.target.value.slice(0, 100))} maxLength={100} />
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <Label>{t("contestant.policyLabel")}</Label>
              <div className="flex items-center gap-3 rounded-lg border border-border p-3">
                <Switch checked={mustChange} onChange={toggleMust} ariaLabel={t("contestant.mustChange")} />
                <span className="text-sm cursor-pointer select-none" onClick={() => toggleMust(!mustChange)}>{t("contestant.mustChange")}</span>
              </div>
              <div className="flex items-center gap-3 rounded-lg border border-border p-3">
                <Switch checked={cannotChange} onChange={toggleCannot} ariaLabel={t("contestant.cannotChange")} />
                <span className="text-sm cursor-pointer select-none" onClick={() => toggleCannot(!cannotChange)}>{t("contestant.cannotChange")}</span>
              </div>
              <p className="text-xs text-muted-foreground">{t("contestant.exclusive")}</p>
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="flex justify-between gap-3 pt-2">
              {isEditing ? (
                <Button type="button" variant="outline" onClick={resetMfa}>{t("account.resetMfa")}</Button>
              ) : <span />}
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => navigate("/liveness/admin/accounts")}>{t("common.cancel")}</Button>
                <Button type="submit" loading={loading}>{isEditing ? t("common.save") : t("common.create")}</Button>
              </div>
            </div>
          </form>
          )}
        </CardContent>
      </Card>

      {/* Facial biometrics — link this account's face so it can sign in with it. */}
      {isEditing && isSuperAdmin && (
        <Card className="w-full mt-4">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-1">
              <ScanFace className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold tracking-tight">{t("account.faceTitle", "Biometria facial")}</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              {t("account.faceHelp2", "Gere um link para esta pessoa cadastrar o rosto, ou vincule a conta a uma biometria já existente. Depois ela poderá entrar pelo rosto na tela de login.")}
            </p>
            <AccountFaceBiometrics userId={id} />
          </CardContent>
        </Card>
      )}

      {credentials && (
        <CredentialsModal email={credentials.email} password={credentials.password}
          mfaSecret={credentials.mfaSecret} mfaQr={credentials.mfaQr}
          onClose={() => navigate("/liveness/admin/accounts")} />
      )}
      {mfaModal && (
        <CredentialsModal email={mfaModal.email} mfaSecret={mfaModal.mfaSecret} mfaQr={mfaModal.mfaQr}
          title="MFA redefinido" onClose={() => setMfaModal(null)} />
      )}
    </div>
  );
}
